// eKasa receipt lookup via public Slovak Financial Administration API.
// QR codes from Slovak eKasa receipts encode the receipt URL.
// Example: https://ekasa.financnasprava.sk/mdu/cic/<receipt-id>

const API_BASE = 'https://ekasa.financnasprava.sk/mdu/api/v1';

function extractReceiptId(qrText) {
  if (!qrText) return null;
  const trimmed = String(qrText).trim();
  const match = trimmed.match(/\/cic\/([^/?#]+)/i);
  if (match) return match[1];
  if (/^[a-zA-Z0-9-]{6,}$/.test(trimmed)) return trimmed;
  return null;
}

function vatRateFromCode(code) {
  if (code == null) return null;
  const s = String(code).toUpperCase();
  if (s === 'A' || s === '20') return 20;
  if (s === 'B' || s === '10') return 10;
  if (s === 'C' || s === '5') return 5;
  if (s === 'D' || s === '0') return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export async function lookupEkasaReceipt(qrText) {
  const receiptId = extractReceiptId(qrText);
  if (!receiptId) {
    throw new Error('Neplatný QR kód — toto nie je eKasa blok.');
  }

  let res;
  try {
    res = await fetch(`${API_BASE}/opd/receipt/find`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ receiptId }),
    });
  } catch (e) {
    throw new Error('eKasa server nedostupný. Skús neskôr alebo zadaj ručne.');
  }

  if (res.status === 404) {
    throw new Error('Blok nebol nájdený v eKasa. Skontroluj QR alebo zadaj ručne.');
  }
  if (!res.ok) {
    throw new Error(`eKasa lookup zlyhal (HTTP ${res.status}).`);
  }

  let data;
  try { data = await res.json(); }
  catch { throw new Error('eKasa vrátila neplatnú odpoveď.'); }

  const r = data?.receipt || data;
  if (!r) throw new Error('eKasa neposlala dáta o bloku.');

  const org = r.organization || r.organisation || r.merchant || {};
  const items = (r.items || r.receiptItems || []).map(it => ({
    name: it.name || it.itemName || '',
    quantity: Number(it.quantity ?? it.qty ?? 1),
    unitPrice: Number(it.unitPrice ?? it.price ?? 0),
    vatRate: vatRateFromCode(it.vatRate ?? it.vat ?? it.taxRate),
    total: Number(it.totalPrice ?? it.total ?? 0),
  }));

  const totalVat = items.reduce((sum, it) => {
    if (it.vatRate == null) return sum;
    return sum + it.total - (it.total / (1 + it.vatRate / 100));
  }, 0);
  const detectedVat = items.length && items[0].vatRate != null ? items[0].vatRate : null;

  let dateStr = r.issueDate || r.createdAt || r.date || null;
  if (dateStr) dateStr = String(dateStr).slice(0, 10);

  return {
    vendor: org.name || r.vendorName || '',
    ico: org.ico || org.businessId || r.organizationId || '',
    dic: org.dic || '',
    amount: Number(r.totalPrice ?? r.amount ?? items.reduce((s, it) => s + it.total, 0)),
    vatAmount: Number(totalVat.toFixed(2)),
    vatRate: detectedVat,
    date: dateStr,
    items,
    raw: data,
  };
}

export { extractReceiptId };
